import { ref, onMounted, onUnmounted } from 'vue'

/**
 * Composable for monitoring network connectivity status
 * Returns isOnline reactive ref and connection type information
 */
export function useNetworkStatus() {
  const isOnline = ref(true)
  const connectionType = ref('unknown')
  let networkListener = null

  const isMobile = window.Capacitor?.isNativePlatform?.()

  onMounted(async () => {
    if (!isMobile) {
      // Web fallback using navigator.onLine
      isOnline.value = navigator.onLine

      const handleOnline = () => {
        isOnline.value = true
        connectionType.value = 'online'
      }

      const handleOffline = () => {
        isOnline.value = false
        connectionType.value = 'offline'
      }

      window.addEventListener('online', handleOnline)
      window.addEventListener('offline', handleOffline)

      // Cleanup for web
      onUnmounted(() => {
        window.removeEventListener('online', handleOnline)
        window.removeEventListener('offline', handleOffline)
      })

      return
    }

    // Mobile: Use Capacitor Network plugin
    try {
      const { Network } = await import('@capacitor/network')

      // Get initial network status
      const status = await Network.getStatus()
      isOnline.value = status.connected
      connectionType.value = status.connectionType

      // Listen for network status changes
      networkListener = await Network.addListener('networkStatusChange', (status) => {
        isOnline.value = status.connected
        connectionType.value = status.connectionType

        console.log('Network status changed:', {
          connected: status.connected,
          connectionType: status.connectionType
        })
      })
    } catch (error) {
      console.warn('Network plugin not available:', error)
      // Fallback to web API
      isOnline.value = navigator.onLine
    }
  })

  onUnmounted(() => {
    // Clean up network listener
    if (networkListener) {
      networkListener.remove()
    }
  })

  return {
    isOnline,
    connectionType,
    isMobile
  }
}
